import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	Output
} from '@angular/core';
import { searchStopReasonText } from '@brand-lolly/app/social-media-influencer/modules/ai-agent-page/consts/search-stop-reasons.consts';
import { AiResultsService } from '@brand-lolly/app/social-media-influencer/modules/ai-agent-page/services/ai-results.service';
import {
	ProjectInfluencerSelectionService,
	SearchJobStatus
} from '@brand-lolly/app/social-media-influencer/modules/ai-agent-page/services/project-influencer-selection.service';
import {
	AiAssistantSearchProgressComponent,
	AiSearchProgressOutcome,
	AiSearchProgressStage,
	AiSearchProgressState
} from 'components';
import { Observable, of, Subject, timer } from 'rxjs';
import {
	catchError,
	exhaustMap,
	map,
	switchMap,
	take,
	takeUntil
} from 'rxjs/operators';

/** Matches the wizard's own job-status cadence on the top-results screen. */
const POLL_INTERVAL_MS = 3000;

/**
 * A single dropped request is a blip, not a failed search. Three in a row with
 * nothing in between is the only failure signal this side of the wire gets:
 * the search endpoint carries no job status, and a job that died leaves its
 * counters frozen rather than saying so.
 */
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

/**
 * How long to keep asking when the project has no search job at all. Ana creates
 * the job before she hands the project id over, so this only covers a pane opened
 * on a project nobody searched — polling that forever would be a leak.
 */
const MAX_IDLE_TICKS = 10;

/** Ordered so the reached stage can be kept as a high-water mark. */
const STAGE_ORDER: AiSearchProgressStage[] = [
	'filters',
	'database',
	'fresh_data',
	'analysis'
];

interface PollSnapshot {
	/** 0-100 as the search endpoint computed it. */
	progress: number;
	jobId: string | null;
	job: SearchJobStatus | null;
}

/**
 * Narrates a running influencer search for the assistant's detail pane.
 *
 * It polls rather than streams, and that is the whole reason it exists as its own
 * component. A search runs for minutes; Ana's live channel is the body of one
 * POST and cannot stay open that long. The pane is not the chat and carries no
 * turn-scoped constraint, so it asks repeatedly instead.
 *
 * Two existing endpoints, no new client: the search endpoint the wizard's
 * top-results screen already polls hands back the job id and the percentage, and
 * the search-job endpoint hands back the counters. The counters are what the
 * stages are read off — nothing here advances on a timer.
 *
 * Lives in the app rather than the library because both services are app-level;
 * the drawing is the library's `app-ai-assistant-search-progress`, handed one
 * prepared object. Host it the way the panel hosts the creator profile: inside an
 * ng-template passed to the pane.
 */
@Component({
	standalone: true,
	selector: 'app-ana-search-progress',
	imports: [AiAssistantSearchProgressComponent],
	template: `
		<app-ai-assistant-search-progress
			[progressState]="progressState"
			(closed)="closed.emit()"
		></app-ai-assistant-search-progress>
	`,
	styles: [
		// language=scss
		`
			:host {
				display: block;
				width: 100%;
				overflow-y: auto;
				scrollbar-width: thin;
				scrollbar-color: rgba(0, 137, 224, 0.3) transparent;
			}
		`
	]
})
export class AnaSearchProgressComponent implements OnDestroy {
	/** Null until the first response lands, which is what draws the placeholders. */
	progressState: AiSearchProgressState | null = null;

	private currentProjectId: string | null = null;
	private jobId: string | null = null;
	/** High-water mark: a stage the counters once proved is never taken back. */
	private stageIndex = 0;
	private consecutiveFailures = 0;
	private idleTicks = 0;
	private stopPolling = new Subject<void>();
	private unsubscriber = new Subject<void>();

	constructor(
		private aiResultsService: AiResultsService,
		private projectInfluencerSelectionService: ProjectInfluencerSelectionService
	) {}

	/**
	 * The project whose search to watch. Setting it to null stops the poll, which
	 * is how a host that keeps this component mounted closes the pane; a host that
	 * destroys it is covered by ngOnDestroy instead.
	 */
	@Input() set projectId(value: string | null | undefined) {
		const nextProjectId = value ?? null;
		if (nextProjectId === this.currentProjectId) {
			return;
		}
		this.currentProjectId = nextProjectId;
		this.resetForNewProject();
		if (nextProjectId) {
			this.startPolling(nextProjectId);
		}
	}

	/**
	 * The search this project's progress is being read off, the moment the first
	 * poll names one.
	 *
	 * It exists because the roster stacked beneath this in the pane needs the same
	 * job id to follow a live search instead of reading the roster once, and this
	 * is the component that already asks the question. The alternative — carrying
	 * the id out of the creating tool's result — only answers it for the turn that
	 * created the project, and says nothing for a project reopened later, a search
	 * restarted by a refinement, or any project reached from the conversation that
	 * this turn did not create.
	 */
	@Output() searchJobIdChanged = new EventEmitter<string | null>();

	@Output() closed = new EventEmitter<void>();

	/**
	 * The search stopped, however it stopped — found what it wanted, ran out of
	 * candidates, analysed nothing, failed.
	 *
	 * This component is the only thing in the workspace watching a running
	 * search, so it is also the only thing that can say when one ends. The
	 * alternative — a second poll in the facade — would be two watchers with
	 * two opinions about "finished", and the subtle half of that judgement
	 * (the job row flips a tick AFTER the results land) lives here.
	 *
	 * Fires ONCE per search: the host starts a conversation turn on it, and a
	 * second emission would start a second one.
	 */
	@Output() searchEnded = new EventEmitter<AiSearchProgressState>();

	private endedReported = false;

	ngOnDestroy(): void {
		this.stopPolling.next();
		this.stopPolling.complete();
		this.unsubscriber.next();
		this.unsubscriber.complete();
	}

	/** Once per search, whichever terminal path got here first. */
	private reportEnded(state: AiSearchProgressState | null): void {
		if (this.endedReported || !state) return;
		this.endedReported = true;
		this.searchEnded.emit(state);
	}

	private resetForNewProject(): void {
		this.endedReported = false;
		this.stopPolling.next();
		this.progressState = null;
		this.jobId = null;
		this.stageIndex = 0;
		this.consecutiveFailures = 0;
		this.idleTicks = 0;
	}

	private startPolling(projectId: string): void {
		timer(0, POLL_INTERVAL_MS)
			.pipe(
				// exhaustMap, not switchMap: a slow tick must not be cancelled and
				// re-issued, or a busy search gets hammered with overlapping reads.
				exhaustMap(() => this.pollOnce(projectId)),
				takeUntil(this.stopPolling),
				takeUntil(this.unsubscriber)
			)
			.subscribe(snapshot => this.applySnapshot(snapshot));
	}

	/** Resolves to null when the tick could not be served at all. */
	private pollOnce(projectId: string): Observable<PollSnapshot | null> {
		return this.aiResultsService
			.startSearch({ search_prompt: '', project_id: projectId })
			.pipe(
				switchMap(searchResult => {
					const jobId = searchResult.searchJobId ?? this.jobId;
					if (!jobId) {
						return of({
							progress: searchResult.progress,
							jobId: null,
							job: null
						});
					}
					// The shared job poll, not a request of this component's own: the
					// roster stacked beneath watches the same job on the same
					// cadence, and two reads of one row is what T-933 measured.
					// `take(1)` reads whatever that poll last saw — already
					// buffered while the roster holds the stream open, its own
					// first request otherwise.
					return this.projectInfluencerSelectionService
						.watchJobStatus(projectId, jobId)
						.pipe(
							take(1),
							// The shared poll reports a tick it could not serve as
							// null. That is a failed read, not "this project has no
							// search" — the distinction the idle path depends on.
							map(job =>
								job
									? {
											progress: searchResult.progress,
											jobId,
											job
									  }
									: null
							)
						);
				}),
				catchError(() => of(null))
			);
	}

	private applySnapshot(snapshot: PollSnapshot | null): void {
		if (!snapshot) {
			this.consecutiveFailures += 1;
			if (this.consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
				this.reportFailure('The search could not be reached.');
			}
			return;
		}
		this.consecutiveFailures = 0;

		if (snapshot.jobId && snapshot.jobId !== this.jobId) {
			this.jobId = snapshot.jobId;
			// Reported from the poll callback, never from the input setter that
			// resets it: the host binds this straight into a sibling in the same
			// template, and writing a sibling's input mid-pass is what
			// ExpressionChangedAfterItHasBeenChecked is for. Clearing it on a new
			// project is the host's own job, since the host is what changed.
			this.searchJobIdChanged.emit(snapshot.jobId);
		}

		if (!snapshot.job) {
			this.reportIdle();
			return;
		}

		this.idleTicks = 0;
		const job = snapshot.job;
		this.stageIndex = Math.max(this.stageIndex, this.stageIndexFor(job));

		const outcome = this.outcomeFor(job, snapshot.progress);
		this.progressState = {
			outcome,
			stage: STAGE_ORDER[this.stageIndex],
			progress: outcome === 'finished' ? 100 : snapshot.progress,
			analysedCount: job.analyzedCount,
			returnedCount: job.completedCount + job.failedCount,
			pendingCount: job.pendingCount,
			foundCount: job.foundGoodCount,
			requestedCount: job.requestedCount,
			failureReason: this.stopReasonFor(job, outcome)
		};

		if (outcome !== 'running') {
			this.stopPolling.next();
			this.reportEnded(this.progressState);
		}
	}

	/**
	 * No job exists for this project. Deliberately NOT reported as "filters
	 * prepared": that stage claims a job row, and there isn't one.
	 */
	private reportIdle(): void {
		this.idleTicks += 1;
		this.progressState = {
			outcome: 'idle',
			stage: 'filters',
			progress: 0,
			analysedCount: 0,
			returnedCount: 0,
			pendingCount: 0,
			foundCount: 0,
			requestedCount: 0
		};
		if (this.idleTicks >= MAX_IDLE_TICKS) {
			this.stopPolling.next();
		}
	}

	/**
	 * Keeps whatever the last good poll proved and marks it stopped, so the reader
	 * still sees what was collected before the failure.
	 */
	private reportFailure(reason: string): void {
		const previous = this.progressState;
		this.progressState = {
			outcome: 'failed',
			stage: STAGE_ORDER[this.stageIndex],
			progress: previous?.progress ?? 0,
			analysedCount: previous?.analysedCount ?? 0,
			returnedCount: previous?.returnedCount ?? 0,
			pendingCount: previous?.pendingCount ?? 0,
			foundCount: previous?.foundCount ?? 0,
			requestedCount: previous?.requestedCount ?? 0,
			failureReason: reason
		};
		this.stopPolling.next();
		// A failed search ends the wait too. Without this the host keeps
		// waiting for a return that the poll has already given up on.
		this.reportEnded(this.progressState);
	}

	/**
	 * Each step up is something a counter proves:
	 * found_good > 0 means matches are being scored, completed/failed > 0 means
	 * analyses are coming back, analyzed > 0 means candidates were dispatched, and
	 * a job with none of those has only its criteria.
	 */
	private stageIndexFor(job: SearchJobStatus): number {
		if (job.foundGoodCount > 0) return 3;
		if (job.completedCount + job.failedCount > 0) return 2;
		if (job.analyzedCount > 0) return 1;
		return 0;
	}

	private outcomeFor(
		job: SearchJobStatus,
		progress: number
	): AiSearchProgressOutcome {
		if (job.status === 'failed' || job.status === 'canceled') {
			return 'failed';
		}
		if (this.projectInfluencerSelectionService.isJobTerminal(job.status)) {
			return 'finished';
		}
		// The search endpoint reports 100 as soon as the shortlist target is met,
		// which lands a tick or two before the job row flips to completed — so
		// take it early, but only against a job reading that agrees with it.
		//
		// The percentage is fetched fresh on every tick while the counters come
		// from the SHARED job poll's replayed last value (`watchJobStatus` +
		// `take(1)`), which runs on its own timer — so the two can be readings of
		// different moments. Trusting the percentage on its own announced an
		// ending with the PREVIOUS reading's counters, and since polling stops
		// the moment the outcome is not `running`, that mismatch froze on screen:
		// a run that met its target in full was headlined "Search finished short"
		// off a stale `found_good_count`, and a genuinely short one lost its stop
		// reason (the older row carried no `stop_reason` yet).
		//
		// A row that does not show the target met is simply older than the
		// number: the backend returns 100 only once the row's own
		// `found_good_count` reached the target, capping every other case at 99
		// (`_build_agentic_response_from_job`). So disagreement means "poll
		// again", not "finished".
		if (progress >= 100 && job.foundGoodCount >= job.requestedCount) {
			return 'finished';
		}
		return 'running';
	}

	/**
	 * Only for a search that did not get what it was asked for. A completed job
	 * stops with goal_reached, and saying so would be noise. The raw exception
	 * text is deliberately not surfaced.
	 */
	private stopReasonFor(
		job: SearchJobStatus,
		outcome: AiSearchProgressOutcome
	): string | null {
		if (outcome === 'running' || outcome === 'idle') return null;
		if (job.foundGoodCount >= job.requestedCount) return null;
		return searchStopReasonText(job.stopReason);
	}
}
